import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";

/**
 * Thin haptics adapter — no-op on web, real feedback on native (Android
 * + iOS via Capacitor). Every call is fire-and-forget: on the rare
 * platforms where the plugin misfires we swallow the promise rejection
 * rather than surface it as a toast.
 *
 * Feel guidelines (kept small on purpose):
 *   - tap()     → primary button, category-chip select, tab change
 *   - select()  → segmented control step, PTR threshold cross
 *   - success() → mutation confirmed, "all settled up", first save
 *   - warning() → destructive confirm, form validation error
 *   - error()   → mutation rollback
 *
 * Reduced-motion is a display preference, not a haptic preference, so
 * we don't gate on it. Users can disable Vibrate in OS settings if
 * they want silence.
 */
function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

async function safe(op: () => Promise<unknown>): Promise<void> {
  if (!isNative()) return;
  try {
    await op();
  } catch {
    /* swallow — haptics are decorative */
  }
}

export const haptics = {
  tap: (): void => void safe(() => Haptics.impact({ style: ImpactStyle.Light })),
  select: (): void => void safe(() => Haptics.selectionChanged()),
  success: (): void =>
    void safe(() => Haptics.notification({ type: NotificationType.Success })),
  warning: (): void =>
    void safe(() => Haptics.notification({ type: NotificationType.Warning })),
  error: (): void =>
    void safe(() => Haptics.notification({ type: NotificationType.Error })),
};
